/**
 * Pre-execution state inspection for bulk order amendments.
 *
 * Orders already in a final state must never be swept into a batch by default —
 * re-amending them escalates to the admin-only `forceCompleteManually`, so the
 * operator has to opt them in one by one. Kept out of the .vue component so the
 * rules are unit-testable (the portal's vitest config has no vue plugin).
 */

/** States that mean the order is already finished; re-amending them needs force. */
export const FINAL_ORDER_STATES = ['COMPLETED', 'COMPLETED_MANUALLY'] as const;

/** Actions the backend restricts to `pay_operations_admin`. */
const ADMIN_ACTIONS = ['forceCompleteManually', 'refundAndCancelManually'];

export interface BulkSelectableOrder {
  orderUuid?: string | null;
  uuid?: string | null;
  orderState?: string | null;
}

export function isCompletedState(state: string | null | undefined): boolean {
  return FINAL_ORDER_STATES.includes(state as (typeof FINAL_ORDER_STATES)[number]);
}

/** Orders carry the uuid under `orderUuid` (cases) or `uuid` (queue rows). */
export function orderKey(order: BulkSelectableOrder): string {
  return order.orderUuid || order.uuid || '';
}

export function completedOrders(orders: BulkSelectableOrder[]): BulkSelectableOrder[] {
  return orders.filter((o) => isCompletedState(o.orderState));
}

/**
 * Initial checkbox state: everything except already-final orders. Spec §3 —
 * `PROBLEM`/`FAILED`/`REJECTED` are confirmed by default, `COMPLETED` and
 * `COMPLETED_MANUALLY` require an explicit tick from the operator.
 */
export function defaultSelection(orders: BulkSelectableOrder[]): string[] {
  return orders
    .filter((o) => !isCompletedState(o.orderState))
    .map(orderKey)
    .filter(Boolean);
}

/** True when at least one final-state order is ticked. */
export function hasCompletedSelected(
  orders: BulkSelectableOrder[],
  selectedUuids: string[],
): boolean {
  return completedOrders(orders).some((o) => selectedUuids.includes(orderKey(o)));
}

/**
 * Escalate `completeManually` to `forceCompleteManually` when the operator has
 * explicitly ticked a final-state order. Any other action passes through — a
 * cancel is never rewritten.
 */
export function escalateAction(
  action: string,
  orders: BulkSelectableOrder[],
  selectedUuids: string[],
): string {
  const requested = action || 'completeManually';
  if (requested !== 'completeManually') return requested;
  return hasCompletedSelected(orders, selectedUuids) ? 'forceCompleteManually' : requested;
}

export function requiresAdmin(action: string): boolean {
  return ADMIN_ACTIONS.includes(action);
}

/**
 * Confirmation payload the backend re-checks. Only produced when the action
 * actually escalated, so an accidental force-complete cannot be acknowledged.
 */
export function buildConfirmations(action: string): Record<string, boolean> {
  return action === 'forceCompleteManually' ? { forceCompleteAcknowledged: true } : {};
}
